/**
 * Live-Debug-Logs BIS ERP — Browser-Sammler.
 *
 * Gegenstueck zum RemoteLogSink der Kassen-App: erfasst, was im Kundenbrowser
 * schiefgeht, und schickt es gebuendelt an POST /debug/client-logs. Der Support
 * sieht es unter /admin/logs in derselben Zeitachse wie die Backend-Ereignisse.
 *
 * Zwei Betriebsmodi:
 *   - Always-On: Abstuerze und 5xx-Antworten gehen IMMER raus. Nur so ist ein
 *     gemeldeter Fehler rueckwirkend nachvollziehbar, ohne dass der Kunde ihn
 *     reproduzieren muss.
 *   - Voll-Debug: console-Ausgaben und jeder API-Aufruf, aber nur solange der
 *     Support den Modus fuer diesen Mandanten eingeschaltet hat. Der Schalter
 *     kommt als Echo in jeder Server-Antwort zurueck — kein eigener Poll noetig.
 *
 * 🚨 Fehlersturm-Riegel: Eine Render-Schleife erzeugt tausende identische Fehler
 * pro Minute. Gleiche Signatur wird deshalb nur EINMAL pro 60 s gesendet, danach
 * zaehlt occurrence_count hoch. Ohne diesen Riegel legt der Sammler die Datenbank
 * lahm — dieselbe Klasse Problem wie der Retention-Delete bei posRemoteLog.
 *
 * Der Sammler benutzt absichtlich `fetch` direkt statt des ApiClient:
 * ein zirkulaerer Import (client.ts meldet Fehler hierher) waere sonst
 * unvermeidbar, und ein Fehler beim Melden wuerde sich selbst melden.
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost/api';

/** Normalbetrieb: alle 5 s. Nach einem Fehler frueher, damit er nicht bei einem Reload verloren geht. */
const FLUSH_INTERVAL_MS = 5000;
const ERROR_FLUSH_DELAY_MS = 800;

/** Gleiche Signatur hoechstens einmal pro Minute als eigene Zeile. */
const DEDUPE_WINDOW_MS = 60000;

/** Mehr nimmt der Server pro Batch ohnehin nicht an. */
const MAX_BATCH = 50;

/** Obergrenze der Warteschlange — daruber wird nur noch gezaehlt, nicht gesammelt. */
const MAX_QUEUE = 100;

export type DebugLevel = 'debug' | 'info' | 'warning' | 'error' | 'critical';

/**
 * Berichtsversand-Links (Bestätigung/Abmeldung) tragen ein Einmal-Token im Pfad — es darf nie in einem Debug-Log landen
 * (auch nicht im Log eines fremden Mandanten, wenn im selben Browser eine BIS-Sitzung besteht). Token → `…`.
 */
function maskiereBerichtsversandToken(text: string): string {
  return text.replace(/(\/(?:berichtsversand\/bestaetigen|public\/berichtsversand\/bestaetigung)\/)[0-9a-fA-F]{16,}/g, '$1…');
}

/**
 * V-2549: Zugangsdaten in Adressen und Meldungen (Query UND Fragment): Token, Zugriffs-/Refresh-Token, Einmal-Code der
 * Google-Anmeldung, PKCE-Verifier, ID-Token. Der Browser trägt die Adresse sonst in jede Fehlermeldung. Wert → `…`.
 */
export function maskiereUrlGeheimnisse(text: string): string {
  return text.replace(
    /(^|[?&#;\s,{"'])([a-z0-9]+_(?:token|code)|access_?token|refresh_?token|id_?token|google_?code|code_?verifier|tenants|token)=[^&#\s"'<>]*/gi,
    '$1$2=…'
  );
}

/** Alle Maskierungen für Text, der in ein Debug-Log wandert (Meldung, Stack, Adresse). */
function maskiereLogText(text: string): string {
  return maskiereUrlGeheimnisse(maskiereBerichtsversandToken(text));
}

interface QueuedEntry {
  logged_at: string;
  level: DebugLevel;
  channel: string;
  message: string;
  stack_trace?: string;
  context?: Record<string, unknown>;
  url: string;
  user_agent: string;
  request_id?: string;
  occurrence_count: number;
}

interface ServerConfig {
  enabled: boolean;
  log_level: DebugLevel;
  channel_filter: string;
}

const LEVEL_PRIORITY: Record<DebugLevel, number> = {
  debug: 1,
  info: 2,
  warning: 3,
  error: 4,
  critical: 5,
};

class DebugLogger {
  private queue = new Map<string, QueuedEntry>();
  private lastSentAt = new Map<string, number>();
  private config: ServerConfig = { enabled: false, log_level: 'debug', channel_filter: '' };
  private flushTimer: number | null = null;
  private installed = false;
  private sending = false;
  private droppedSinceLastFlush = 0;

  /** Verhindert, dass der Sammler die eigenen console-Aufrufe wieder einfaengt. */
  private reentrant = false;

  // ===========================================================================
  // Oeffentliche API
  // ===========================================================================

  /**
   * Haengt sich an die globalen Fehlerquellen. Mehrfacher Aufruf ist unschaedlich.
   */
  install(): void {
    if (this.installed || typeof window === 'undefined') return;
    this.installed = true;

    window.addEventListener('error', (event: ErrorEvent) => {
      // Fehlgeschlagene Ressourcen (Bild/Script) liefern kein `error`-Objekt.
      const err = event.error as Error | undefined;
      this.capture({
        level: 'error',
        channel: 'window',
        message: err?.message || event.message || 'Unbekannter Skriptfehler',
        stack: err?.stack,
        context: { source: event.filename, line: event.lineno, column: event.colno },
      });
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message =
        reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : 'Unbehandelte Promise-Ablehnung';
      this.capture({
        level: 'error',
        channel: 'promise',
        message,
        stack: reason instanceof Error ? reason.stack : undefined,
      });
    });

    this.wrapConsole();
    this.startTimer();
    void this.loadConfig();
  }

  /**
   * Meldet eine abgefangene React-Render-Ausnahme.
   *
   * Genau hier lag die Luecke: die ChunkErrorBoundary zeigt „Die Anwendung wurde
   * aktualisiert" und verschluckte bisher die eigentliche Ausnahme — im Support
   * blieb nur Raten oder der Browser-Harness.
   */
  reportRenderError(error: Error, componentStack?: string): void {
    this.capture({
      level: 'critical',
      channel: 'render',
      message: error.message || 'Render-Ausnahme',
      stack: [error.stack, componentStack].filter(Boolean).join('\n--- Komponenten ---\n') || undefined,
      immediate: true,
    });
  }

  /**
   * Meldet eine fehlgeschlagene API-Antwort. `requestId` stammt aus dem
   * X-Request-Id-Header und verbindet diese Zeile mit der Backend-Zeile
   * desselben Aufrufs.
   */
  reportApiError(params: {
    method: string;
    path: string;
    status: number;
    requestId?: string | null;
    detail?: string;
  }): void {
    const isServerError = params.status >= 500 || params.status === 0;
    this.capture({
      level: isServerError ? 'error' : 'info',
      channel: 'network',
      message: `${params.method} ${params.path} → ${params.status || 'Netzwerkfehler'}${params.detail ? ': ' + params.detail : ''}`,
      requestId: params.requestId || undefined,
      context: { method: params.method, path: params.path, status: params.status },
      immediate: isServerError,
    });
  }

  /** Erfolgreicher API-Aufruf — nur im Voll-Debug interessant. */
  reportApiCall(method: string, path: string, status: number, durationMs: number, requestId?: string | null): void {
    if (!this.config.enabled) return;
    this.capture({
      level: 'debug',
      channel: 'network',
      message: `${method} ${path} → ${status} (${durationMs} ms)`,
      requestId: requestId || undefined,
      context: { method, path, status, duration_ms: durationMs },
    });
  }

  isFullDebugActive(): boolean {
    return this.config.enabled;
  }

  // ===========================================================================
  // Erfassung
  // ===========================================================================

  private capture(input: {
    level: DebugLevel;
    channel: string;
    message: string;
    stack?: string;
    context?: Record<string, unknown>;
    requestId?: string;
    immediate?: boolean;
  }): void {
    try {
      if (this.reentrant || typeof window === 'undefined') return;

      const isError = LEVEL_PRIORITY[input.level] >= LEVEL_PRIORITY.error;

      // Ohne aktiven Voll-Debug interessieren nur echte Fehler.
      if (!isError && !this.passesFullDebugFilter(input.level, input.channel)) return;

      // Ohne Anmeldung gibt es keinen Mandanten — der Ingest wuerde 401 liefern.
      if (!this.hasSession()) return;

      const message = (input.message || '').slice(0, 8000);
      if (!message.trim()) return;

      const signature = this.signature(input.level, input.channel, message);
      const now = Date.now();

      const queued = this.queue.get(signature);
      if (queued) {
        queued.occurrence_count++;
        return;
      }

      // Fehlersturm-Riegel: gleiche Signatur innerhalb des Fensters bereits gesendet.
      const lastSent = this.lastSentAt.get(signature);
      if (lastSent !== undefined && now - lastSent < DEDUPE_WINDOW_MS) {
        this.droppedSinceLastFlush++;
        return;
      }

      if (this.queue.size >= MAX_QUEUE) {
        this.droppedSinceLastFlush++;
        return;
      }

      this.queue.set(signature, {
        logged_at: new Date().toISOString(),
        level: input.level,
        channel: input.channel.slice(0, 60),
        message: maskiereLogText(message),
        stack_trace: input.stack ? maskiereLogText(input.stack.slice(0, 20000)) : undefined,
        context: input.context,
        url: maskiereLogText(window.location.href).slice(0, 500),
        user_agent: navigator.userAgent.slice(0, 300),
        request_id: input.requestId,
        occurrence_count: 1,
      });

      if (input.immediate || isError) {
        this.scheduleFlush(ERROR_FLUSH_DELAY_MS);
      }
    } catch {
      // Der Sammler darf die Anwendung unter keinen Umstaenden stoeren.
    }
  }

  private passesFullDebugFilter(level: DebugLevel, channel: string): boolean {
    if (!this.config.enabled) return false;
    if (LEVEL_PRIORITY[level] < LEVEL_PRIORITY[this.config.log_level]) return false;
    if (this.config.channel_filter) {
      return this.config.channel_filter
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
        .some((needle) => channel.toLowerCase().includes(needle));
    }
    return true;
  }

  /**
   * Grobe Signatur fuer das clientseitige Zusammenfassen. Der Server bildet
   * seinen eigenen fingerprint — hier geht es nur darum, einen Sturm zu bremsen.
   */
  private signature(level: string, channel: string, message: string): string {
    const normalized = message
      .replace(/[0-9a-fA-F]{8}-[0-9a-fA-F-]{27,}/g, '<uuid>')
      .replace(/\b\d+\b/g, '<n>')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 300);
    return `${level}|${channel}|${normalized}`;
  }

  private hasSession(): boolean {
    try {
      return !!localStorage.getItem('auth_token');
    } catch {
      return false;
    }
  }

  /**
   * console.error/warn spiegeln, ohne das Original zu verlieren. Im Voll-Debug
   * zusaetzlich console.log — sonst waere der Modus fuer die Fehlersuche wertlos.
   */
  private wrapConsole(): void {
    const wrap = (method: 'error' | 'warn' | 'log', level: DebugLevel) => {
      const original = console[method].bind(console);
      console[method] = (...args: unknown[]) => {
        original(...args);
        try {
          if (this.reentrant) return;
          // console.log nur im Voll-Debug — sonst flutet jede Seite das Protokoll.
          if (method === 'log' && !this.config.enabled) return;

          const stack = args.find((a): a is Error => a instanceof Error)?.stack;
          this.capture({
            level,
            channel: 'console',
            message: args.map((a) => this.stringify(a)).join(' '),
            stack,
          });
        } catch {
          // egal
        }
      };
    };

    wrap('error', 'error');
    wrap('warn', 'warning');
    wrap('log', 'debug');
  }

  private stringify(value: unknown): string {
    if (typeof value === 'string') return value;
    if (value instanceof Error) return `${value.name}: ${value.message}`;
    try {
      return JSON.stringify(value)?.slice(0, 2000) ?? String(value);
    } catch {
      return String(value);
    }
  }

  // ===========================================================================
  // Versand
  // ===========================================================================

  private startTimer(): void {
    if (this.flushTimer !== null) return;
    this.flushTimer = window.setInterval(() => void this.flush(), FLUSH_INTERVAL_MS);

    // Beim Verlassen der Seite noch schnell wegschicken (best effort).
    window.addEventListener('pagehide', () => void this.flush());
  }

  private scheduleFlush(delayMs: number): void {
    window.setTimeout(() => void this.flush(), delayMs);
  }

  private async flush(): Promise<void> {
    if (this.sending || this.queue.size === 0) return;
    if (!this.hasSession()) {
      this.queue.clear();
      return;
    }

    this.sending = true;
    const entries = Array.from(this.queue.values()).slice(0, MAX_BATCH);
    const signatures = Array.from(this.queue.keys()).slice(0, MAX_BATCH);
    this.queue.clear();

    const now = Date.now();
    signatures.forEach((sig) => this.lastSentAt.set(sig, now));
    this.pruneDedupeMap(now);

    if (this.droppedSinceLastFlush > 0) {
      // Verschwiegene Verwerfungen wuerden wie „alles ruhig" aussehen — also mitschicken.
      entries.push({
        logged_at: new Date().toISOString(),
        level: 'info',
        channel: 'collector',
        message: `${this.droppedSinceLastFlush} weitere gleichartige Meldungen unterdrückt (Fehlersturm-Riegel)`,
        url: maskiereLogText(window.location.href).slice(0, 500),
        user_agent: navigator.userAgent.slice(0, 300),
        occurrence_count: 1,
      });
      this.droppedSinceLastFlush = 0;
    }

    try {
      this.reentrant = true;
      const token = localStorage.getItem('auth_token');
      const tenantId = localStorage.getItem('tenant_id');

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (tenantId) headers['X-Tenant-ID'] = tenantId;

      const response = await fetch(`${API_URL}/debug/client-logs`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ entries }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.config) this.applyConfig(data.config);
      }
    } catch {
      // Netzwerkfehler: Eintraege sind verloren. Erneutes Puffern wuerde bei
      // dauerhaftem Ausfall unbegrenzt wachsen — der Verlust ist hier das
      // kleinere Uebel als ein volllaufender Browser-Speicher.
    } finally {
      this.reentrant = false;
      this.sending = false;
    }
  }

  private pruneDedupeMap(now: number): void {
    if (this.lastSentAt.size < 500) return;
    this.lastSentAt.forEach((timestamp, key) => {
      if (now - timestamp > DEDUPE_WINDOW_MS) this.lastSentAt.delete(key);
    });
  }

  private async loadConfig(): Promise<void> {
    if (!this.hasSession()) return;
    try {
      this.reentrant = true;
      const token = localStorage.getItem('auth_token');
      const tenantId = localStorage.getItem('tenant_id');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (tenantId) headers['X-Tenant-ID'] = tenantId;

      const response = await fetch(`${API_URL}/debug/client-config`, {
        method: 'GET',
        headers,
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data?.config) this.applyConfig(data.config);
      }
    } catch {
      // Ohne Config bleibt es beim Always-On-Betrieb — voellig ausreichend.
    } finally {
      this.reentrant = false;
    }
  }

  private applyConfig(config: Partial<ServerConfig>): void {
    this.config = {
      enabled: config.enabled === true,
      log_level: (config.log_level as DebugLevel) || 'debug',
      channel_filter: config.channel_filter || '',
    };
  }
}

export const debugLogger = new DebugLogger();
